import React, { useState, useEffect } from "react";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getClassesAPI,
  getClassScheduleByClassAPI,
  createClassScheduleAPI,
  updateClassScheduleAPI,
  deleteClassScheduleAPI,
} from "../../../services/portal/admin/classService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";
import {
  convertFileToBase64,
  formatDateTime,
  getStatusBadge,
  tableHeaderColor,
} from "../../../utility/Common";
import ClassDetailsBox from "../../../components/portal/ClassDetailsBox";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RoutePaths } from "../../../utility/RoutePaths";

const ClassScheduleManagePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const classId = searchParams.get("classId");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classSchedules, setClassSchedules] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(classId ? classId : 0);
  const [classDetails, setClassDetails] = useState(null);
  const [editingDetails, setEditingDetails] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [formData, setFormData] = useState({
    classId: 0,
    startDate: "",
    endDate: "",
    recordLink: "",
    description: "",
    zoomLink: "",
    zoomPassword: "",
    status: 1,
    image: null,
    isFree: false,
    isNewImage: false,
  });

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    if (selectedClassId > 0) {
      loadClassSchedules(selectedClassId);
    } else {
      setClassSchedules([]);
    }
  }, [selectedClassId]);

  useEffect(() => {
    if (classes.length > 0) {
      const classItem = classes.find(
        (c) => c.ClassId === Number(selectedClassId),
      );
      if (classItem) {
        setClassDetails(classItem);
      } else {
        setClassDetails(null);
      }
    }
  }, [selectedClassId, classes]);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setClasses(res.Data);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
      showErrorToastMsg("Failed to load classes");
    } finally {
      setLoading(false);
    }
  };

  const loadClassSchedules = async (classId) => {
    setLoading(true);
    try {
      const res = await getClassScheduleByClassAPI(classId);
      if (res && res.IsSuccessful && res.Data) {
        setClassSchedules(res.Data);
      } else {
        setClassSchedules([]);
      }
    } catch (error) {
      console.error("Error loading class schedules:", error);
      showErrorToastMsg("Failed to load class schedules");
      setClassSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData({
        ...formData,
        [name]: files[0] || null,
        isNewImage: true,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.classId || Number(formData.classId) === 0) {
      showErrorToastMsg("Please select a class");
      return;
    }

    if (!formData.startDate) {
      showErrorToastMsg("Please select a start date and time");
      return;
    }

    if (!formData.endDate) {
      showErrorToastMsg("Please select an end date and time");
      return;
    }

    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      showErrorToastMsg("Start date must be before end date");
      return;
    }

    setSaving(true);

    var imageBase64 = null;
    if (formData.image && formData.isNewImage) {
      imageBase64 = await convertFileToBase64(formData.image);
    } else {
      imageBase64 = formData.image;
    }

    try {
      var params = {
        classId: Number(formData.classId),
        startDate: formData.startDate,
        description: formData.description,
        endDate: formData.endDate,
        recordLink: formData.recordLink.trim(),
        zoomLink: formData.zoomLink.trim(),
        Password: formData.zoomPassword.trim(),
        status: Number(formData.status),
        isFree: formData.isFree
      };

      if (imageBase64) {
        params.image = imageBase64;
      }

      let res;
      if (editingDetails) {
        params.classScheduleId = editingDetails.ClassScheduleId;
        res = await updateClassScheduleAPI(params);
      } else {
        res = await createClassScheduleAPI(params);
      }

      if (res && res.IsSuccessful) {
        showSuccessToastMsg(
          res.Message ||
            (editingDetails
              ? "Class schedule updated successfully"
              : "Class schedule saved successfully"),
        );
        clearData();
        if (selectedClassId > 0) {
          loadClassSchedules(selectedClassId);
        }
      } else {
        showErrorToastMsg(
          res?.Message || "Something went wrong, Please try again later",
        );
      }
    } catch (error) {
      console.error("Error saving class schedule:", error);
      showErrorToastMsg("Failed to save class schedule");
    } finally {
      setSaving(false);
    }
  };

  const handleEditDetails = (scheduleItem) => {
    setEditingDetails(scheduleItem);
    const scheduleStartDate = scheduleItem.StartDate;
    const scheduleEndDate = scheduleItem.EndDate;

    // Format datetime for input (YYYY-MM-DDTHH:mm)
    let formattedStartDate = "";
    let formattedEndDate = "";

    if (scheduleStartDate) {
      const dateObj = new Date(scheduleStartDate);
      // Format: YYYY-MM-DDTHH:mm (datetime-local format)
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const hours = String(dateObj.getHours()).padStart(2, "0");
      const minutes = String(dateObj.getMinutes()).padStart(2, "0");
      formattedStartDate = `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    if (scheduleEndDate) {
      const dateObj = new Date(scheduleEndDate);
      // Format: YYYY-MM-DDTHH:mm (datetime-local format)
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const hours = String(dateObj.getHours()).padStart(2, "0");
      const minutes = String(dateObj.getMinutes()).padStart(2, "0");
      formattedEndDate = `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    setFormData({
      classId: Number(scheduleItem.ClassId || scheduleItem.classId),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      recordLink: scheduleItem.RecordLink || "",
      zoomLink: scheduleItem.ZoomLink || "",
      zoomPassword: scheduleItem.Password || "",
      description: scheduleItem.Description || "",
      image: scheduleItem.Image || null,
      status: Number(scheduleItem.Status),
      isFree: scheduleItem.IsFree,
    });
  };

  const clearData = () => {
    setEditingDetails(null);
    setFormData({
      classId: selectedClassId || 0,
      startDate: "",
      endDate: "",
      recordLink: "",
      zoomLink: "",
      zoomPassword: "",
      description: "",
      image: null,
      status: 1,
      isNewImage: false,
      isFree: false,
    });
  };

  const handleDelete = (scheduleItem) => {
    setDeleteItem(scheduleItem);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const scheduleId = deleteItem.ClassScheduleId;
      const res = await deleteClassScheduleAPI(scheduleId);

      if (res.IsSuccessful) {
        if (selectedClassId > 0) {
          loadClassSchedules(selectedClassId);
        }
        showSuccessToastMsg("Class schedule deleted successfully");
      } else {
        showErrorToastMsg(res.Message || "Failed to delete class schedule");
      }

      setShowDeleteConfirm(false);
      setDeleteItem(null);
    } catch (error) {
      console.error("Error deleting class schedule:", error);
      showErrorToastMsg("Failed to delete class schedule");
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  const openDocumentPage = (scheduleId) => {
    navigate(
      `${RoutePaths.ADMIN_SCHEDULE_DOCUMENT_MANAGE_PAGE}?classId=${classId}&scheduleId=${scheduleId}`,
    );
  };

  return (
    <DashboardLayout
      title="Class Schedule Management"
      subtitle="Manage class schedules and links"
    >
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-6">
          Manage Class Schedules
        </h3>

        {/* Filter by Class */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div className="p-6">
            <div className="flex items-center gap-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Class:
              </label>
              <select
                value={selectedClassId}
                onChange={(e) => {
                  const classId = Number(e.target.value);
                  setSelectedClassId(classId);
                  setFormData({
                    ...formData,
                    classId: classId,
                  });
                }}
                className="text-sm font-medium w-300 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="0">Select a class</option>
                {classes.map((classItem) => (
                  <option
                    key={classItem.ClassId}
                    value={Number(classItem.ClassId)}
                  >
                    {classItem.Name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {classDetails && <ClassDetailsBox classDetails={classDetails} />}

        {selectedClassId > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
            <div
              className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
            >
              <h4 className="text-md font-medium text-white">
                {editingDetails ? "Edit Schedule" : "Add Schedule"}
              </h4>
            </div>
            <div className="p-6">
              <form
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date & Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date & Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter description"
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Record Link
                  </label>
                  <input
                    type="url"
                    name="recordLink"
                    value={formData.recordLink}
                    onChange={handleChange}
                    placeholder="Enter record link"
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zoom Link
                  </label>
                  <input
                    type="url"
                    name="zoomLink"
                    value={formData.zoomLink}
                    onChange={handleChange}
                    placeholder="Enter zoom link"
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zoom Password
                  </label>
                  <input
                    type="text"
                    name="zoomPassword"
                    value={formData.zoomPassword}
                    onChange={handleChange}
                    placeholder="Enter zoom password"
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Free/Paid <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="isFree"
                    value={formData.isFree}
                    onChange={handleChange}
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="false">Paid</option>
                    <option value="true">Free</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image
                  </label>
                  <input
                    type="file"
                    name="image"
                    onChange={handleChange}
                    accept="image/*,.pdf"
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {formData.image && (
                    <p className="text-xs text-gray-600 mt-1">
                      Selected: {formData.image.name}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Accepted formats: Image files (JPG, PNG)
                  </p>
                </div>

                <div className="flex gap-2 items-end justify-end md:col-span-3">
                  {editingDetails && (
                    <button
                      type="button"
                      onClick={clearData}
                      className="text-sm font-medium px-4 py-1.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={saving}
                    className={`text-sm font-medium px-4 py-1.5 rounded-md transition-colors ${
                      saving
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-primary-600 text-white hover:bg-primary-700"
                    }`}
                  >
                    {saving
                      ? "Saving..."
                      : editingDetails
                        ? "Update"
                        : "Add Schedule"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {selectedClassId > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div
              className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
            >
              <h4 className="text-md font-medium text-white">Schedules List</h4>
            </div>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex justify-end gap-3 items-center">
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by description, start date or end date..."
                  className="text-sm w-[500px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {searchText && (
                  <button
                    type="button"
                    onClick={() => setSearchText("")}
                    className="text-sm px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Start Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      End Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Free/Paid
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Record Link
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Zoom Link
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Zoom Password
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                      >
                        <Loader />
                      </td>
                    </tr>
                  ) : classSchedules.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                      >
                        No class schedules found.
                      </td>
                    </tr>
                  ) : (
                    classSchedules
                      .filter((s) => {
                        if (!searchText) return true;
                        const q = searchText.toLowerCase();
                        return (
                          (s.Description || "").toLowerCase().includes(q) ||
                          formatDateTime(s.StartDate).toLowerCase().includes(q) ||
                          formatDateTime(s.EndDate).toLowerCase().includes(q)
                        );
                      })
                      .map((scheduleItem) => (
                      <tr
                        key={scheduleItem.ClassScheduleId}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatDateTime(scheduleItem.StartDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatDateTime(scheduleItem.EndDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {scheduleItem.Image ? (
                            <img
                              src={
                                scheduleItem.Image.startsWith("data:")
                                  ? scheduleItem.Image
                                  : `data:image/jpeg;base64,${scheduleItem.Image}`
                              }
                              alt="Schedule"
                              className="w-16 h-16 object-cover rounded"
                            />
                          ) : (
                            <img
                              src={"/images/no_image.jpg"}
                              alt="Schedule"
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {scheduleItem.Description}
                        </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {scheduleItem.IsFree ? "Free" : "Paid"}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {scheduleItem.RecordLink ? (
                            <a
                              href={scheduleItem.RecordLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 underline truncate block max-w-xs"
                            >
                              Record Link
                            </a>
                          ) : (
                            ""
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {scheduleItem.ZoomLink ? (
                            <a
                              href={scheduleItem.ZoomLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 underline truncate block max-w-xs"
                            >
                              Zoom Link
                            </a>
                          ) : (
                            ""
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {scheduleItem.Password || ""}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {getStatusBadge(scheduleItem.Status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-4">
                          <button
                            onClick={() => handleEditDetails(scheduleItem)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => openDocumentPage(scheduleItem.ClassScheduleId)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            Documents
                          </button>

                          <button
                            onClick={() => handleDelete(scheduleItem)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <RemoveConfirmationDialog
            loading={loading}
            description={
              formatDateTime(deleteItem.StartDate) +
              " - " +
              formatDateTime(deleteItem.EndDate)
            }
            cancelDelete={cancelDelete}
            confirmDelete={confirmDelete}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClassScheduleManagePage;
